import React, { useState } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import classes from './styles.module.scss';
import FaqFormModal from './FaqFormModal';
import DeleteConfirm from '../councellingStudentContainer/features/DeleteConfirm';
import { actionNotifier } from '../../components/ui/toast';

const formatDate = (value) => {
    if (!value) {
        return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '-';
    }

    return date.toLocaleString();
};

const SiteFaqContainer = ({
    faqList,
    isLoading,
    onDeleteFaq,
    onEditFaq,
    onSaveFaq,
    refetchFaqList,
    search,
    sectionOptions,
    selectedSection,
    setSelectedSection,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [deleteId, setDeleteId] = useState(null);

    const filteredFaqList = faqList.filter((item) => {
        if (!search.trim()) {
            return true;
        }

        const normalizedSearch = search.toLowerCase();
        return [
            item?.section,
            item?.category,
            item?.question,
            item?.answer,
        ].some((field) => field?.toLowerCase().includes(normalizedSearch));
    });

    const handleOpenCreateModal = () => {
        setEditingItem(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setEditingItem(null);
        setIsModalOpen(false);
    };

    const handleEditClick = async (faq) => {
        const faqDetails = await onEditFaq(faq);
        setEditingItem(faqDetails);
        setIsModalOpen(true);
    };

    const handleDeleteClick = (id) => {
        setDeleteId(id);
    };

    const handleConfirmDelete = async () => {
        if (!deleteId) {
            return;
        }

        try {
            await onDeleteFaq(deleteId);
            setDeleteId(null);
        } catch (error) {
            actionNotifier.error(error?.data?.message || 'Unable to delete FAQ.');
        }
    };

    return (
        <div className={classes.faq_page_container}>
            <div className={classes.page_header}>
                <div className={classes.filter_group}>
                    <select
                        value={selectedSection}
                        onChange={(event) => setSelectedSection(event.target.value)}
                        className={classes.section_select}
                    >
                        <option value="">All Sections</option>
                        {sectionOptions.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>

                    <button
                        type="button"
                        className={classes.refresh_btn}
                        onClick={refetchFaqList}
                    >
                        <RefreshIcon fontSize="small" /> Refresh
                    </button>
                </div>

                <button
                    type="button"
                    className={classes.add_new_btn_main}
                    onClick={handleOpenCreateModal}
                >
                    <AddCircleOutlineIcon fontSize="small" /> Add FAQ
                </button>
            </div>

            <div className={classes.faq_list_container}>
                {filteredFaqList.length > 0 ? (
                    <div className={classes.table_wrapper}>
                        <table className={classes.faq_table}>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Section</th>
                                    <th>Category</th>
                                    <th>Question</th>
                                    <th>Answer</th>
                                    <th>Sort Order</th>
                                    <th>Created At</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredFaqList.map((item) => (
                                    <tr key={item.id}>
                                        <td>{item.id}</td>
                                        <td>{item.section || '-'}</td>
                                        <td>{item.category || '-'}</td>
                                        <td className={classes.question_cell}>{item.question || '-'}</td>
                                        <td className={classes.answer_cell}>{item.answer || '-'}</td>
                                        <td>{item.sortOrder ?? '-'}</td>
                                        <td>{formatDate(item.createdAt)}</td>
                                        <td>
                                            <div className={classes.action_buttons}>
                                                <button
                                                    type="button"
                                                    className={classes.edit_btn}
                                                    onClick={() => handleEditClick(item)}
                                                >
                                                    <EditOutlinedIcon fontSize="small" /> Edit
                                                </button>
                                                <button
                                                    type="button"
                                                    className={classes.delete_btn}
                                                    onClick={() => handleDeleteClick(item.id)}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" /> Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={classes.empty_state}>
                        No FAQ records found for the current filters.
                    </div>
                )}
            </div>

            <FaqFormModal
                open={isModalOpen}
                handleClose={handleCloseModal}
                handleSave={onSaveFaq}
                initialData={editingItem}
                isLoading={isLoading}
            />

            {deleteId !== null ? (
                <DeleteConfirm
                    isModalOpen={true}
                    handleModal={handleConfirmDelete}
                    setIsModalOpen={() => setDeleteId(null)}
                    title="FAQ"
                />
            ) : null}
        </div>
    );
};

export default SiteFaqContainer;
